import { Spinner } from "../../../../ui/spinner";
import { useAgentForm } from "./AgentFormContext";
import FormCardFrame from "./FormCardFrame";
import FormEditor from "./FormEditor";
import type { FormCard, FormValues } from "./formTypes";
import useFormDraft from "./useFormDraft";

type Props = {
  card: FormCard;
  /** Re-opening a submitted card: start from what was submitted. */
  initial?: FormValues;
  /** Present when re-opening; drops the edit and returns to the summary. */
  onCancel?: () => void;
};

/**
 * A FORM card, live — see `FormEditor`.
 *
 * What this adds is the draft: unsubmitted values are saved per session and card
 * (`useFormDraft`) and restored when the card is drawn again. The read is
 * asynchronous, and the editor takes its starting values once, so the fields are only
 * mounted after it has finished — a control that seeds its own state from its value
 * (the pasted link, say) would otherwise be mounted empty and never learn better.
 */
const FormAnswer = ({ card, initial, onCancel }: Props) => {
  const { sessionId } = useAgentForm();
  const editing = !!onCancel;
  const {
    saved,
    save: saveDraft,
    clear: clearDraft,
  } = useFormDraft(sessionId, card.formId, !editing);

  // `saved` is null until the read lands. A re-opened card has its own values.
  const start = editing ? (initial ?? {}) : saved;
  if (!start) {
    return (
      <FormCardFrame label={card.prompt}>
        <Spinner size="small" />
      </FormCardFrame>
    );
  }

  return (
    <FormEditor
      card={card}
      initial={start}
      onCancel={onCancel}
      saveDraft={saveDraft}
      clearDraft={clearDraft}
    />
  );
};

export default FormAnswer;
